import {STATUS} from '@jobseeker/common-core/constants';
import axiosInterceptorInstance from '@jobseeker/common-core/helpers/axiosInterceptor';

import type {
  JobDetailRequest,
  JobApplyRequest,
  JobFollowupRequest,
  PerksAndBenefitsRequest,
} from '@jobseeker/common-core/reducers/job/thunkServices/requestInterfaces';
import type {JobDetail} from '@jobseeker/common-core/types';
import {enqueueSnackbar} from 'notistack';

const axios = axiosInterceptorInstance(
  `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_JOB_SERVICE}`
);

const getJobDetails = async ({
  jobId,
  headers,
  ...params
}: JobDetailRequest): Promise<JobDetail> => {
  try {
    const response = await axios.get(`/detail`, {
      params: {...params, jobcode: jobId},
      headers: headers,
    });
    return response.data.data;
  } catch (error: unknown) {
    throw error;
  }
};

const fetchEpochCompanies = async (): Promise<JobDetail[]> => {
  try {
    const response = await axios.get(`/getUnpublishedJobs`, {
      params: {
        tags: 1183547,
      },
    });

    return response?.data?.data;
  } catch (error) {
    console.log('Failed to fetch Companies for Epoch Round 2', error);
    throw error;
  }
};

const jobApply = async ({
  jobId,
  ...body
}: JobApplyRequest): Promise<JobDetail> => {
  try {
    const response = await axios.post(`/apply?jobcode=${jobId}`, {
      ...body,
    });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

const getAppliedJobCount = async (params: any, headers?: any) => {
  try {
    const response = await axios.get(
      `/applied/count/`,
      headers ? {params, headers} : {params}
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const doFollowUp = async ({jobId}: JobFollowupRequest) => {
  try {
    const response: any = await axios.post(`/followup?jobcode=${jobId}`, {});
    if (response[STATUS] === 200) {
      return true;
    }
  } catch (error: any) {
    enqueueSnackbar(error.response?.data.error.message, {variant: 'error'});
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
  return;
};

const reportJob = async ({jobId, ...body}: any) => {
  try {
    const response: any = await axios.post(`report?id=${jobId}&action=job`, {
      ...body,
    });
    if (response[STATUS] === 200) {
      return response;
    }
  } catch (error: any) {
    enqueueSnackbar(error.response?.data.error.message, {variant: 'error'});
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
  return;
};

const tagIdBySlug = async (slug: string) => {
  try {
    const response: any = await axios.get(`/taginfo?slug=${slug}`, {});
    if (response[STATUS] === 200) {
      return response['data']['data'][0].id;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
  return;
};

const markJobViewed = async (jobId: string | number) => {
  try {
    const response: any = await axios.get(`/view?jobcode=${jobId}`, {});
  } catch (error: any) {
    console.error(error);
  }
};

const saveJob = async (jobId: string | number, unsave = false) => {
  const url = `/${unsave ? 'unsave' : 'save'}/?jobcode=${jobId}`;
  try {
    const response = await axios.post(url, {});
    return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

const getAssessmentDetail = async (jobId: string | number) => {
  try {
    const response: any = await axios.get(`/assessment?jobcode=${jobId}`, {});
    return response.data;
  } catch (error: any) {
    // console.error(error);
    return error?.response?.data;
  }
};

const markAssessmentComplete = async (jobId: string | number) => {
  try {
    const response: any = await axios.post(
      `/assessment_complete?jobcode=${jobId}`,
      {}
    );
    return response.data;
  } catch (error: any) {
    // console.error(error);
    return error?.response?.data;
  }
};

const getPerksAndBenefits = async ({
  companyId,
  headers,
}: PerksAndBenefitsRequest): Promise<object> => {
  try {
    const response = await axios.get(`/getPerksAndBenefits`, {
      params: {companyId},
      headers: headers,
    });
    return response.data.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};
const getAmbitionBoxData = async (slug: string) => {
  try {
    const response: any = await axios.get(`/company-data?slug=${slug}`, {});
    console.log('Ambition Box resp', response);
    return response.data;
  } catch (error: any) {
    // console.error(error);
    return error?.response?.data;
  }
};

const getCompanyIdBySlug = async (slug: string) => {
  try {
    const response: any = await axios.get(`/company-id-slug?slug=${slug}`, {});
    return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

const getCompanySlugById = async (id: number) => {
  try {
    const response: any = await axios.get(`/company-slug?id=${id}`, {});
    return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

const applyToMultipleJobs = async (jobIds: string[], body: JobApplyRequest) => {
  const response: any = await axios.post('/apply-multiple', {jobIds, ...body});
  return response.data;
};

const jobDetailService = {
  saveJob,
  getJobDetails,
  jobApply,
  getAppliedJobCount,
  doFollowUp,
  tagIdBySlug,
  reportJob,
  markJobViewed,
  getAssessmentDetail,
  markAssessmentComplete,
  getPerksAndBenefits,
  getAmbitionBoxData,
  getCompanyIdBySlug,
  getCompanySlugById,
  applyToMultipleJobs,
  fetchEpochCompanies,
};

export default jobDetailService;
